import { useState, type FormEvent } from 'react';
import { ErreurApi, api } from '../lib/apiClient';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { Badge, Champ, Modale } from '../components/Elements';
import { MenuActions, type ActionMenu } from '../components/MenuActions';
import { PiedFormulaire } from './ReferentielsPages';
import { ChoixPersonnel } from '../components/ChoixPersonnel';
import { formaterDuree, formaterHeure, libelleJour } from '../lib/format';
import type { Activite, Creneau, Lieu, Personnel } from '../types';

const JOURS_OUVRES = [1, 2, 3, 4, 5, 6, 7];

/** « 16:30 » + 45 → « 17:15 ». Même calcul que côté serveur. */
function ajouterMinutes(heure: string, minutes: number): string {
  const [h, m] = heure.split(':').map(Number);
  const total = Math.min((h ?? 0) * 60 + (m ?? 0) + minutes, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Création et modification d'un créneau.
 *
 * On planifie « un cours de piano de 30 minutes le mercredi à 16h30 »,
 * pas « de 16h30 à 17h00 » : la saisie porte donc sur l'heure de début et
 * la durée, et l'heure de fin s'affiche calculée. La durée est
 * pré-remplie depuis l'activité — mais reste modifiable, un même
 * instrument ayant réellement des séances de 30 à 60 minutes.
 */
export function FormulaireCreneau({
  ouverte,
  anneeId,
  anneeLibelle,
  creneau,
  activites,
  personnel,
  lieux,
  onFermer,
  onEnregistre,
  onChangerStatut,
  onEcrire,
}: {
  ouverte: boolean;
  anneeId: string;
  anneeLibelle: string;
  /** `null` pour une création. */
  creneau: Creneau | null;
  activites: Activite[];
  personnel: Personnel[];
  lieux: Lieu[];
  onFermer: () => void;
  onEnregistre: () => void;
  /**
   * Fermer ou rouvrir le créneau, depuis le menu ⋮ de la modale. C'est le
   * PARENT qui appelle l'API et recharge : le formulaire ne fait que
   * demander, comme pour le transfert d'une inscription. Absent = pas de
   * menu.
   */
  onChangerStatut?: (creneau: Creneau, actif: boolean) => void;
  /**
   * « Écrire aux élèves » (2026-10-08), depuis le même menu : le parent
   * ouvre la modale de destinataires une fois la fiche refermée.
   */
  onEcrire?: (creneau: Creneau) => void;
}) {
  const { peut } = useAuth();
  const [valeurs, setValeurs] = useState({
    activiteId: '',
    encadrantIds: [] as string[],
    lieuId: '',
    jour: 3,
    heureDebut: '17:00',
    dureeMinutes: 60,
    capaciteMax: '',
    commentaire: '',
  });
  const [dureeTouchee, setDureeTouchee] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = ouverte ? (creneau?.id ?? 'nouveau') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setDureeTouchee(Boolean(creneau));
    setValeurs({
      activiteId: creneau?.activite_id ?? '',
      encadrantIds: (creneau?.encadrants ?? []).map((e) => e.personnel_id),
      lieuId: creneau?.lieu_id ?? '',
      jour: creneau?.jour ?? 3,
      heureDebut: creneau?.heure_debut.slice(0, 5) ?? '17:00',
      dureeMinutes: creneau?.duree_minutes ?? 60,
      // Vide = « celle de l'activite ». Une capacite heritee ne se
      // pre-remplit donc pas, sinon la rouvrir la figerait sur le creneau.
      capaciteMax:
        creneau && !creneau.capacite_heritee && creneau.capacite_max
          ? String(creneau.capacite_max)
          : '',
      commentaire: creneau?.commentaire ?? '',
    });
    setErreur(null);
  }
  if (!ouverte && initialisePour !== null) setInitialisePour(null);

  const activite = activites.find((a) => a.id === valeurs.activiteId);

  // La durée suit l'activité choisie, tant que l'utilisateur ne l'a pas
  // ajustée lui-même : c'est ce qui rend la saisie rapide sans l'enfermer.
  if (activite?.duree_minutes && !dureeTouchee && valeurs.dureeMinutes !== activite.duree_minutes) {
    setValeurs((etat) => ({ ...etat, dureeMinutes: activite.duree_minutes! }));
  }

  const heureFin = ajouterMinutes(valeurs.heureDebut, valeurs.dureeMinutes);

  // Avertissement de conflit avant enregistrement : mieux vaut le voir à
  // la saisie qu'après coup dans la liste des conflits.
  const { donnees: existants } = useRessource(
    () =>
      ouverte && valeurs.jour
        ? api.get<Creneau[]>('/api/creneaux', { anneeId, jour: valeurs.jour })
        : Promise.resolve([]),
    [ouverte, anneeId, valeurs.jour],
  );

  const chevauche = (existants ?? []).filter((autre) => {
    if (autre.id === creneau?.id) return false;
    const communs = autre.encadrants.some((e) => valeurs.encadrantIds.includes(e.personnel_id));
    if (!communs) return false;
    return (
      valeurs.heureDebut < autre.heure_fin.slice(0, 5) && heureFin > autre.heure_debut.slice(0, 5)
    );
  });

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const corps = {
        // Toujours envoyee, meme vide : c'est ce qui permet de detacher
        // tous les encadrants d'un creneau.
        encadrantIds: valeurs.encadrantIds,
        lieuId: valeurs.lieuId || null,
        jour: valeurs.jour,
        heureDebut: valeurs.heureDebut,
        dureeMinutes: valeurs.dureeMinutes,
        capaciteMax: valeurs.capaciteMax ? Number(valeurs.capaciteMax) : null,
        commentaire: valeurs.commentaire || null,
      };

      if (creneau) {
        await api.patch(`/api/creneaux/${creneau.id}`, {
          ...corps,
          // Envoyee seulement si elle a change : le serveur refuse le
          // changement sur un creneau porteur d'inscriptions.
          ...(valeurs.activiteId !== creneau.activite_id ? { activiteId: valeurs.activiteId } : {}),
        });
      } else {
        await api.post('/api/creneaux', {
          ...corps,
          anneeId,
          activiteId: valeurs.activiteId,
        });
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  // Un creneau ferme se lit, il ne se modifie pas : on le rouvre d'abord.
  const inactif = Boolean(creneau && !creneau.actif);

  return (
    <Modale
      titre={
        creneau ? (
          <span className="flex items-center gap-2">
            {inactif ? 'Créneau inactif' : 'Modifier le créneau'}
            {inactif && <Badge ton="alerte">inactif</Badge>}
          </span>
        ) : (
          'Nouveau créneau'
        )
      }
      titreAccessible={creneau ? (inactif ? 'Créneau inactif' : 'Modifier le créneau') : undefined}
      ouverte={ouverte}
      onFermer={onFermer}
      /**
       * Le menu ⋮, a gauche de la croix (2026-09-29). Deux entrees :
       * ecrire aux eleves (2026-10-08), et une bascule — fermer un creneau
       * actif, rouvrir un creneau ferme. Chacune suit sa permission ; sans
       * aucune, pas de menu du tout (MenuActions vide ne s'affiche pas).
       *
       * `fermer` est la fermeture GARDEE : une saisie en cours demande
       * confirmation avant que l'action ne parte.
       */
      actions={(fermer) => {
        if (!creneau) return null;
        const entrees: ActionMenu[] = [];
        if (onEcrire && peut('message.envoyer')) {
          entrees.push({
            cle: 'ecrire',
            libelle: 'Écrire aux élèves',
            aide: 'Aux inscrits actifs et à leurs contacts, depuis Gmail',
            icone: <IconeEnveloppe />,
            onChoisir: () => {
              fermer();
              onEcrire(creneau);
            },
          });
        }
        if (onChangerStatut && peut('creneau.modifier')) {
          entrees.push(
            inactif
              ? {
                  cle: 'rouvrir',
                  libelle: 'Rendre le créneau actif',
                  aide: 'Il revient au planning et à la saisie d’inscription',
                  icone: <IconeLecture />,
                  onChoisir: () => {
                    fermer();
                    onChangerStatut(creneau, true);
                  },
                }
              : {
                  cle: 'fermer',
                  libelle: 'Rendre le créneau inactif',
                  aide: 'Il sort du planning et de la saisie ; refusé s’il a des inscrits actifs',
                  icone: <IconePause />,
                  onChoisir: () => {
                    if (
                      !window.confirm(
                        'Le créneau n’apparaîtra plus au planning ni à la saisie d’inscription. ' +
                          'Il reste dans la liste des créneaux et peut être rouvert. Continuer ?',
                      )
                    ) {
                      return;
                    }
                    fermer();
                    onChangerStatut(creneau, false);
                  },
                },
          );
        }
        return <MenuActions actions={entrees} />;
      }}
    >
      <form onSubmit={soumettre} className="space-y-4">
        {inactif && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Ce créneau est inactif : il n’apparaît ni au planning ni à la saisie d’inscription.
            Pour le modifier, rouvrez-le depuis le menu ⋮.
          </p>
        )}
        {/* Un creneau ferme est en lecture seule : le `fieldset` grise
            tout d'un coup, et le pied ne propose plus que de fermer. */}
        <fieldset disabled={inactif} className="space-y-4">
        {/* L'annee ne se saisit pas ici : elle vient de l'ecran, et un
            creneau n'en change pas. Mais elle doit se VOIR — depuis que la
            page peut afficher une annee passee, rien ne disait dans
            laquelle on ecrivait. */}
        <Champ
          libelle="Année scolaire"
          aide={
            creneau
              ? 'L’année d’un créneau ne se change pas. Pour le reporter, reprenez la grille sur une autre année.'
              : 'Celle affichée à l’écran. Pour créer sur une autre, changez d’année en haut de la page.'
          }
        >
          <input
            className="champ bg-encre-50 text-encre-600"
            value={anneeLibelle}
            readOnly
            aria-readonly="true"
          />
        </Champ>

        {/* L'activite reste modifiable tant que le creneau ne porte AUCUNE
            inscription — meme condition que la suppression. Au-dela, la
            changer laisserait des inscriptions rattachees a un cours
            qu'elles ne suivent pas ; la cle etrangere a trois colonnes de
            `inscription` le refuse en base, la route le dit en clair. */}
        {creneau && creneau.effectif + creneau.effectif_historique > 0 ? (
          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-sm">
            <span className="font-medium">{creneau.activite_libelle}</span>
            <span className="ml-2 text-xs text-encre-500">
              {creneau.effectif + creneau.effectif_historique} inscription(s) : l’activité ne peut
              plus changer. Créez un autre créneau, ou réaffectez-les.
            </span>
          </p>
        ) : (
          <Champ
            libelle="Activité"
            aide={
              creneau
                ? 'Modifiable tant que le créneau ne porte aucune inscription.'
                : 'Elle détermine la durée par défaut, et l’entité du créneau.'
            }
          >
            <select
              className="champ"
              value={valeurs.activiteId}
              onChange={(e) => {
                setValeurs({ ...valeurs, activiteId: e.target.value });
                setDureeTouchee(false);
              }}
              required
              autoFocus={!creneau}
            >
              <option value="">— Choisir —</option>
              {activites
                .filter((a) => a.actif)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.libelle}
                    {a.duree_minutes ? ` — ${formaterDuree(a.duree_minutes)}` : ''}
                  </option>
                ))}
            </select>
          </Champ>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Jour">
            <select
              className="champ"
              value={valeurs.jour}
              onChange={(e) => setValeurs({ ...valeurs, jour: Number(e.target.value) })}
            >
              {JOURS_OUVRES.map((jour) => (
                <option key={jour} value={jour}>
                  {libelleJour(jour)}
                </option>
              ))}
            </select>
          </Champ>

          <Champ libelle="Heure de début">
            <input
              className="champ tabulaire"
              type="time"
              step={300}
              value={valeurs.heureDebut}
              onChange={(e) => setValeurs({ ...valeurs, heureDebut: e.target.value })}
              required
            />
          </Champ>
        </div>

        <Champ
          libelle="Durée (minutes)"
          aide={
            activite?.duree_minutes
              ? `Durée standard de ${activite.libelle} : ${formaterDuree(activite.duree_minutes)}${activite.duree_heritee ? ' (héritée de son type)' : ''}.`
              : 'Aucune durée standard connue pour cette activité.'
          }
        >
          <input
            className="champ tabulaire"
            type="number"
            min={5}
            max={480}
            step={5}
            value={valeurs.dureeMinutes}
            onChange={(e) => {
              setDureeTouchee(true);
              setValeurs({ ...valeurs, dureeMinutes: Number(e.target.value) || 0 });
            }}
            required
          />
        </Champ>

        {/* L'heure de fin n'est pas saisie : elle se déduit. L'afficher
            évite d'avoir à la calculer de tête. */}
        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-sm">
          <span className="text-encre-500">Le cours se tiendra le </span>
          <span className="tabulaire font-medium">
            {libelleJour(valeurs.jour).toLowerCase()} de {formaterHeure(valeurs.heureDebut)} à{' '}
            {formaterHeure(heureFin)}
          </span>
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Encadrement" aide="Le premier est le titulaire — le planning regroupe sous son nom.">
            <div className="space-y-2">
              {valeurs.encadrantIds.map((personnelId, indice) => (
                <ChoixPersonnel
                  key={`${personnelId}-${indice}`}
                  personnel={personnel}
                  valeur={personnelId}
                  placeholder="Taper un nom…"
                  onChanger={(id) =>
                    setValeurs((etat) => {
                      const liste = [...etat.encadrantIds];
                      if (id) liste[indice] = id;
                      else liste.splice(indice, 1);
                      return { ...etat, encadrantIds: liste };
                    })
                  }
                />
              ))}
              {/* Une seule ligne vierge a la fois : elle disparait des
                  qu'elle est remplie, remplacee par la suivante. */}
              <ChoixPersonnel
                key={`vide-${valeurs.encadrantIds.length}`}
                personnel={personnel.filter((s) => !valeurs.encadrantIds.includes(s.id))}
                valeur=""
                placeholder={
                  valeurs.encadrantIds.length === 0
                    ? 'Non attribué — taper un nom…'
                    : 'Ajouter un encadrant…'
                }
                onChanger={(id) =>
                  id &&
                  setValeurs((etat) => ({ ...etat, encadrantIds: [...etat.encadrantIds, id] }))
                }
              />
            </div>
          </Champ>

          <Champ libelle="Lieu">
            <select
              className="champ"
              value={valeurs.lieuId}
              onChange={(e) => setValeurs({ ...valeurs, lieuId: e.target.value })}
            >
              <option value="">— Non défini —</option>
              {lieux
                .filter((l) => l.actif)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.code} — {l.libelle}
                  </option>
                ))}
            </select>
          </Champ>
        </div>

        {chevauche.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">Un encadrant est déjà occupé sur ce créneau</p>
            <ul className="mt-1 list-inside list-disc text-xs">
              {chevauche.map((autre) => (
                <li key={autre.id}>
                  {autre.activite_libelle} — {formaterHeure(autre.heure_debut)} à{' '}
                  {formaterHeure(autre.heure_fin)}
                  {autre.lieu_code && ` (${autre.lieu_code})`}
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs">
              L’enregistrement reste possible : le planning le signalera comme conflit.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Champ
            libelle="Capacité maximale"
            aide={
              activite?.capacite_max
                ? `Laissez vide pour reprendre celle de l’activité : ${activite.capacite_max} places. À renseigner si la salle en accueille moins.`
                : 'Facultatif — l’activité n’en fixe aucune. Le planning signale les dépassements.'
            }
          >
            <input
              className="champ tabulaire"
              type="number"
              min={1}
              max={999}
              placeholder={activite?.capacite_max ? String(activite.capacite_max) : ''}
              value={valeurs.capaciteMax}
              onChange={(e) => setValeurs({ ...valeurs, capaciteMax: e.target.value })}
            />
          </Champ>

          <Champ libelle="Commentaire">
            <input
              className="champ"
              value={valeurs.commentaire}
              onChange={(e) => setValeurs({ ...valeurs, commentaire: e.target.value })}
            />
          </Champ>
        </div>
        </fieldset>

        {inactif ? (
          <div className="flex justify-end pt-2">
            <button type="button" className="bouton-secondaire" onClick={onFermer}>
              Fermer
            </button>
          </div>
        ) : (
          <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
        )}
      </form>
    </Modale>
  );
}

/** Deux barres : mettre en pause, fermer sans supprimer. */
function IconePause() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
      <rect x="3" y="2.5" width="3.5" height="11" rx="1" />
      <rect x="9.5" y="2.5" width="3.5" height="11" rx="1" />
    </svg>
  );
}

/** Enveloppe : écrire. */
function IconeEnveloppe() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="m2.5 4.5 5.5 4 5.5-4" />
    </svg>
  );
}

/** Triangle : reprendre. */
function IconeLecture() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
      <path d="M4 2.5v11l9-5.5-9-5.5Z" />
    </svg>
  );
}

/** CDC §5.3 : préparer l'année suivante en recopiant la grille des cours. */
export function FormulaireDuplication({
  ouverte,
  anneeCibleId,
  anneeCibleLibelle,
  annees,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  anneeCibleId: string;
  anneeCibleLibelle: string;
  annees: { id: string; libelle: string }[];
  onFermer: () => void;
  onEnregistre: (bilan: { crees: number; ignores: number }) => void;
}) {
  const [source, setSource] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const candidates = annees.filter((a) => a.id !== anneeCibleId);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const bilan = await api.post<{ crees: number; ignores: number }>(
        '/api/creneaux/duplication',
        { anneeSourceId: source, anneeCibleId },
      );
      onEnregistre(bilan);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Duplication impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Reprendre les créneaux d’une autre année" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        {candidates.length === 0 ? (
          <p className="text-sm text-encre-500">
            Aucune autre année scolaire sur cette entité. Créez-en une avant de dupliquer.
          </p>
        ) : (
          <>
            <Champ libelle="Année source" aide="La grille à recopier.">
              <select
                className="champ"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                required
                autoFocus
              >
                <option value="">— Choisir —</option>
                {candidates.map((annee) => (
                  <option key={annee.id} value={annee.id}>
                    {annee.libelle}
                  </option>
                ))}
              </select>
            </Champ>

            {/* La cible suit l'ecran. Sans ce rappel, on recopie sans
                savoir ou — et l'erreur ne se voit qu'apres coup. */}
            <Champ
              libelle="Année cible"
              aide="Celle affichée à l’écran. Pour viser une autre année, changez-en en haut de la page."
            >
              <input
                className="champ bg-encre-50 text-encre-600"
                value={anneeCibleLibelle}
                readOnly
                aria-readonly="true"
              />
            </Champ>

            <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
              Les créneaux sont recopiés avec leur jour, horaire, professeur et lieu. Les
              inscriptions ne suivent pas. Un créneau déjà présent n’est pas dupliqué, et les
              activités désactivées sont ignorées.
            </p>
          </>
        )}

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} libelle="Reprendre" />
      </form>
    </Modale>
  );
}
